<template>
    <div>
        <div class="sm:flex sm:justify-between sm:items-center mb-10 mt-3">
            <div class="mb-4 sm:mb-0">
                <h1 class="text-2xl font-semibold text-primary">
                    My Credit Score
                </h1>
            </div>
        </div>

        <div class="grid grid-cols-12 gap-4 py-4">
            <div class="col-span-12">
                <ul class="mb-6 flex flex-row text-xs md:text-sm">
                    <li
                        v-for="(user, index) in users"
                        class="pr-3 text-orange"
                        :class="{ 'text-orange': activeUser.id === user.id }"
                        :key="'user-' + index"
                    >
                        <a
                            :href="'#tab-' + index"
                            @click.prevent="setActiveUser(user)"
                            role="button"
                            class="btn-base pill"
                            :class="{ dark: activeUser.id === user.id }"
                            :id="`hs-credit-score-user-${user.id}`"
                        >
                            {{ user.name }}'s Credit Score
                        </a>
                    </li>
                </ul>
            </div>
        </div>

        <creditscore-analytics
            :empty-state="!loadingUserCreditScore && (!userScoreData || !userScoreData.all_scores || userScoreData.all_scores.length === 0)"
        ></creditscore-analytics>
        
        <ScoresTable 
            :empty-state="!loadingUserCreditScore && (!userScoreData || !userScoreData.all_scores || userScoreData.all_scores.length === 0)" 
            :paginated-scores="paginatedScores"
        />

        <div v-if="totalPages > 1 && !(!loadingUserCreditScore && (!userScoreData || !userScoreData.all_scores || userScoreData.all_scores.length === 0)) && !loadingUserCreditScore" class="flex justify-between items-center w-full mt-6">
            <div class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5">
                Showing {{ startIndex + 1 }}-{{ Math.min(endIndex, totalRecords) }} of {{ totalRecords }} records
            </div>

            <div class="flex items-center gap-1">
                <button
                    @click.prevent="currentPage <= 1 ? null : previous()"
                    class="flex w-9 h-9 items-center justify-center rounded hover:bg-transparent group"
                    :class="{
                        'cursor-not-allowed': currentPage <= 1,
                        'cursor-pointer': currentPage > 1
                    }"
                >
                    <Icon 
                        icon="chevron-left-small" 
                        svgClass="w-4 h-4 group-hover:text-black"
                        :class="currentPage <= 1 ? 'text-gray-500 dark:text-gray-400' : 'text-slate-800 dark:text-slate-100'"
                    />
                </button>

                <button
                    v-for="page in visiblePages"
                    :key="page"
                    @click.prevent="pagination(page)"
                    class="flex w-9 h-9 items-center justify-center rounded text-sm font-medium"
                    :class="currentPage === page
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-700 dark:text-slate-200'"
                >
                    {{ page }}
                </button>

                <button
                    @click.prevent="currentPage >= totalPages ? null : next()"
                    class="flex w-9 h-9 items-center justify-center rounded hover:bg-transparent group"
                    :class="{
                        'cursor-not-allowed': currentPage >= totalPages,
                        'cursor-pointer': currentPage < totalPages
                    }"
                >
                    <Icon 
                        icon="chevron-right-small" 
                        svgClass="w-4 h-4 group-hover:text-black" 
                        :class="currentPage >= totalPages ? 'text-gray-500 dark:text-gray-400' : 'text-slate-800 dark:text-slate-100'" 
                    />
                </button>
            </div>
        </div>

        <credit-score-modal @close="closeCreditScoreModal" />
    </div>
</template>
<script>
import { mapState, mapActions } from "pinia";
import { useCreditScoreStore } from "@/store/creditscore";
import CreditscoreAnalytics from "@/components/creditscore/analytics.vue";
import ScoresTable from "@/components/creditscore/scores-table.vue";
import CreditScoreModal from "@/components/creditscore/modals/credit-score.vue";
import Icon from "@/components/ui/icon/icon.vue";

export default {
    props: {},
    components: {
        CreditscoreAnalytics,
        ScoresTable,
        CreditScoreModal,
        Icon,
    },
    data() {
        return {
            perPage: 10,
            currentPage: 1,
            startIndex: 0,
            endIndex: 10,
        };
    },
    computed: {
        ...mapState(useCreditScoreStore, [
            "loadingCreditScoreUsers",
            "loadingUserCreditScore",
            "showCreditScoreModal",
            "users",
            "tabState",
            "activeUser",
            "activeScore",
            "userScoreData",
        ]),
        totalPages() {
            if (!this.userScoreData || !this.userScoreData.all_scores) return 1;
            return Math.ceil(this.userScoreData.all_scores.length / this.perPage);
        },
        totalRecords() {
            if (!this.userScoreData || !this.userScoreData.all_scores) return 0;
            return this.userScoreData.all_scores.length;
        },
        paginatedScores() {
            if (!this.userScoreData || !this.userScoreData.all_scores) return [];
            
            const sortedScores = [...this.userScoreData.all_scores].sort((a, b) => {
                const dateA = new Date(a.score_date);
                const dateB = new Date(b.score_date);
                return dateB - dateA;
            });
            
            return sortedScores.slice(this.startIndex, this.endIndex);
        },
        visiblePages() {
            const pages = [];
            for (let i = 1; i <= this.totalPages; i++) {
                if (i === 1 || i === this.totalPages || Math.abs(i - this.currentPage) <= 2) {
                    pages.push(i);
                }
            }
            return pages;
        },
    },
    watch: {
        activeUser() {
            this.getUserScores();
        },
        currentPage() {
            this.startIndex = (this.currentPage - 1) * this.perPage;
            this.endIndex = this.startIndex + this.perPage;
        },
    },
    methods: {
        ...mapActions(useCreditScoreStore, [
            "setUsers",
            "getUserScores",
            "setTabState",
            "setActiveUser",
            "openCreditScoreModal",
            "closeCreditScoreModal",
        ]),
        applyDeepLinkParams() {
            const params = new URLSearchParams(window.location.search);
            const userIdParam = params.get('user_id');
            const action = params.get('action');

            if (userIdParam && this.users && this.users.length) {
                const targetId = parseInt(userIdParam, 10);
                const targetUser = this.users.find((u) => u.id === targetId);
                if (targetUser) {
                    this.setActiveUser(targetUser);
                }
            }

            if (action === 'update') {
                this.openCreditScoreModal();
            }

            if (userIdParam || action) {
                history.replaceState({}, document.title, window.location.pathname);
            }
        },
        previous() {
            if (this.currentPage > 1) {
                this.currentPage--;
            }
        },
        next() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
            }
        },
        pagination(page) {
            this.currentPage = page;
        },
    },
    mounted() {
        this.setUsers().then(() => {
            if (this.users && this.users.length > 0) {
                this.setActiveUser(this.users[0]);
                this.applyDeepLinkParams();
            }
        }).catch((error) => {
            console.error('Error loading credit score users:', error);
            // Handle the error gracefully - the component will show empty state
        });
    },
};
</script>
